/**
 * User Menu Component
 *
 * Shows login button when not authenticated, or user menu when logged in.
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';

interface UserMenuProps {
  onLoginClick: () => void;
  onAdminPanelClick?: () => void;
  onProfileClick?: () => void;
  onMyPhotosClick?: () => void;
  onAboutClick?: () => void;
}

export default function UserMenu({ onLoginClick, onAdminPanelClick, onProfileClick, onMyPhotosClick, onAboutClick }: UserMenuProps) {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  // Two-step Sign Out confirm, so an accidental tap doesn't end the session
  // (QA S-03).
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setConfirmingLogout(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Drop the confirm state whenever the menu closes for any reason.
  useEffect(() => {
    if (!isOpen) setConfirmingLogout(false);
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
      setConfirmingLogout(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="user-menu">
        <button className="user-menu-button" disabled>
          <span className="user-icon">...</span>
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="user-menu user-menu-anon">
        {/* About/info affordance for signed-out users. Without this, mobile
            users have no path to About once the onboarding pill is dismissed
            (the header About button is desktop-only). See uplift plan §1.4. */}
        {onAboutClick && (
          <button
            className="user-menu-about-btn"
            onClick={onAboutClick}
            aria-label="About this app and its data sources"
            title="About & data sources"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        )}
        <button className="user-menu-button login-button" onClick={onLoginClick}>
          <span className="user-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </span>
          <span className="login-text">Sign In</span>
        </button>
      </div>
    );
  }

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="user-menu-button authenticated"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="user-avatar">
          {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
        </span>
        {user?.isAdmin && <span className="admin-badge" title="Admin">A</span>}
      </button>

      {isOpen && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header">
            <div className="user-name">{user?.name || 'User'}</div>
            <div className="user-email">{user?.email}</div>
            {user?.isAdmin && <div className="user-role">Administrator</div>}
          </div>

          <div className="user-menu-divider" />

          <button className="user-menu-item" role="menuitem" onClick={() => { setIsOpen(false); onProfileClick?.(); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            My Profile
          </button>

          {/* Only render when actually wired — previously this was always
              shown but onMyPhotosClick was never passed, so tapping it did
              nothing (a no-op menu item erodes trust). See uplift plan §1.4. */}
          {onMyPhotosClick && (
            <button className="user-menu-item" role="menuitem" onClick={() => { setIsOpen(false); onMyPhotosClick(); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              My Photos
            </button>
          )}

          {onAboutClick && (
            <button className="user-menu-item" role="menuitem" onClick={() => { setIsOpen(false); onAboutClick(); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              About &amp; data sources
            </button>
          )}

          {user?.isAdmin && (
            <>
              <div className="user-menu-divider" />
              <button className="user-menu-item admin" role="menuitem" onClick={() => { setIsOpen(false); onAdminPanelClick?.(); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
                Admin Panel
              </button>
            </>
          )}

          {/* Appearance / theme (QA S-02). The most accessible home for the
              toggle when signed in — the About menu is buried here. */}
          <div className="user-menu-divider" />
          <div className="user-menu-theme">
            <span className="user-menu-theme-label">Appearance</span>
            <ThemeToggle />
          </div>

          <div className="user-menu-divider" />

          {confirmingLogout ? (
            <div className="user-menu-logout-confirm" role="group" aria-label="Confirm sign out">
              <span className="user-menu-logout-prompt">Sign out of Firewatch?</span>
              <div className="user-menu-logout-actions">
                <button
                  className="user-menu-logout-cancel"
                  onClick={() => setConfirmingLogout(false)}
                >
                  Cancel
                </button>
                <button
                  className="user-menu-logout-confirm-btn"
                  onClick={handleLogout}
                >
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <button
              className="user-menu-item logout"
              role="menuitem"
              onClick={() => setConfirmingLogout(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          )}
        </div>
      )}
    </div>
  );
}
